import './Matrix.scss';
import React, { FC, Suspense, lazy, useEffect } from 'react';
import { useQuery } from '@apollo/client';
import type {
    GetMatrixQuery,
    Cluster as ClusterFields,
    Matrix as MatrixFields,
    CreateMatrixInput as NewNodeFields,
} from '@/generated/graphql';
import type { ClusterCreateObject } from '@/components/stage/Cluster/clusterCreateObject';
import GetMatrix from './graphql/Matrix.gql';
import Cluster from '@/components/stage/Cluster/Cluster';
import clusterCreateObject from '@/components/stage/Cluster/clusterCreateObject';
import StageShell from '@/components/stage/_common/StageShell/StageShell';
import { useUserCurrent } from '@/components/provider/UserCurrentProvider';
import Loading from '@/components/basic/Loading/Loading';
import { concatenateIdToPath, markContentReady, snakeToCamel } from '@/utils';

const EditShell = lazy(
    () =>
        import(
            /* webpackChunkName: "edit-main" */ '@/components/edit/EditShell/EditShell'
        ),
);
const MatrixEdit = lazy(
    () => import(/* webpackChunkName: "edit-main" */ './MatrixEdit'),
);

type MatrixProps = {
    matrixId: number;
    isIndependent?: boolean;
};

const Matrix: FC<MatrixProps> = ({ matrixId, isIndependent = false }) => {
    const { userCurrent } = useUserCurrent();
    const { loading, error, data } = useQuery<GetMatrixQuery>(GetMatrix, {
        variables: { matrixId },
    });

    useEffect(() => {
        if (!loading && !error && !!data) markContentReady();
    }, [loading, error, data]);

    if (loading) {
        return <Loading type="page" />;
    }

    if (error || !data) {
        return <pre>Error...{JSON.stringify(error, null, 2)}</pre>;
    }

    const matrix: MatrixFields = data.matrix;

    const hasCanonicalData = matrix.canonicalZone?.[0]?.canonicalRealm?.[0];

    const canonicalPath =
        !isIndependent && hasCanonicalData
            ? [
                  matrix.canonicalZone[0].canonicalRealm[0].pathId,
                  concatenateIdToPath({
                      id: matrix.canonicalZone[0].id,
                      pathId: matrix.canonicalZone[0].pathId,
                  }),
                  concatenateIdToPath({
                      id: matrix.id,
                      pathId: matrix.pathId,
                  }),
              ]
            : [];

    const clusterObjects = matrix.clusters.map((cluster: ClusterFields) =>
        clusterCreateObject(cluster),
    );

    const currentNodes = [];
    const clusters = clusterObjects.map(
        (cluster: ClusterCreateObject, index) => {
            currentNodes.push({
                nodeId: cluster.clusterFields.id,
                adminTitle: cluster.clusterFields.adminTitle,
            });
            return <Cluster key={`cluster${index}`} cluster={cluster} />;
        },
    );

    const stageShellContent = (
        <StageShell
            componentName="Matrix"
            className={[
                snakeToCamel(matrix.kind),
                snakeToCamel(matrix.structure),
            ]}
            fields={matrix}
            canonicalPath={canonicalPath}
        >
            {clusters}
        </StageShell>
    );

    if (userCurrent?.isEditMode === 'MATRIX') {
        const nodeIds = currentNodes.map((node) => node.nodeId);

        const passEditState = {
            editKey: matrixId,
            componentType: 'MATRIX',
            fields: {
                published: matrix.published,
                publishAll: false,
                autoUpdatePathId: false,
            },
            localFields: {
                title: matrix.title,
                pathId: matrix.pathId,
                canonicalNodeId: matrix.canonicalZone[0]?.id,
                structure: matrix.structure,
                kind: matrix.kind,
                leadText: matrix.leadText,
                bannerText: matrix.bannerText,
                bannerPhoto: {
                    id: matrix.bannerPhoto?.[0]?.id,
                    caption: matrix.bannerPhoto?.[0]?.caption,
                    copyright: matrix.bannerPhoto?.[0]?.copyright?.id,
                    copyrightUrl: matrix.bannerPhoto?.[0]?.copyrightUrl,
                    imageFile: null,
                },
            },
            matrixId: matrixId,
            publishedLocked: matrix.publishedLocked,
            ownerId: matrix.owner.id,

            // newNodeFields is needed to stop error in AnnexCreateNodeFooter.tsx
            // @ createZoneMatrixVariables & createRealmZoneVariables
            // even though it is not used in MatrixEdit.tsx or createMatrixClusterVariables
            newNodeFields: {} as NewNodeFields,

            // needed for deleting and sorting panels
            currentNodes: currentNodes,

            nodesFlaggedForRemoval: [],
            nodesFlaggedForUnlinking: [],

            // for sorting
            nodeIds: nodeIds, // must remain unchanged for comparison.
            sortedNodeIds: nodeIds,
            hasResortedNodes: false,
        };
        return (
            <Suspense fallback={stageShellContent}>
                <EditShell passEditState={passEditState}>
                    <MatrixEdit
                        matrix={matrix}
                        canonicalPath={canonicalPath}
                        clusters={clusters}
                    />
                </EditShell>
            </Suspense>
        );
    }

    return stageShellContent;
};

export default Matrix;
